import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { PasswordInput } from './ui/password-input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Loader2 } from 'lucide-react';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setError(null); // Clear error when user types
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    // Preserve form data in case of error
    const currentFormData = { ...formData };

    try {
      await login(formData);
      // Only clear form data on successful login
      setFormData({ email: '', password: '' });
      navigate('/');
    } catch (err: any) {
      // Ensure form data is preserved after error
      setFormData(currentFormData);
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 sm:py-12 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-md w-full space-y-6 sm:space-y-8'>
        {/* Brand Section */}
        <div className='text-center'>
          <div className='flex justify-center mb-4'>
            <div className='w-16 h-16 bg-gradient-to-br from-primary to-blue-600 rounded-2xl flex items-center justify-center shadow-lg'>
              <span className='text-white font-bold text-2xl'>P</span>
            </div>
          </div>
          <h2 className='heading-2 text-gray-900 mb-2'>Welcome Back</h2>
          {/* <p className='body-small text-gray-600'>
            Sign in to continue to your AI-powered assistant
          </p> */}
        </div>

        <Card className='card-enhanced shadow-xl border-0'>
          <CardContent className='space-y-6 px-6 sm:px-8 py-8'>
            {error && (
              <div className='alert-enhanced alert-error'>
                <div className='flex items-center gap-2'>
                  <span className='text-red-500'>⚠️</span>
                  <span>{error}</span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className='space-y-6'>
              <div className='form-field'>
                <Label htmlFor='email' className='form-label'>
                  Email Address
                </Label>
                <Input
                  id='email'
                  name='email'
                  type='email'
                  autoComplete='email'
                  required
                  placeholder='Enter your email'
                  value={formData.email}
                  onChange={handleChange}
                  className='form-input focus-ring transition-smooth'
                />
              </div>

              <div className='form-field'>
                <div className='flex items-center justify-between mb-2'>
                  <Label htmlFor='password' className='form-label'>
                    Password
                  </Label>
                  <RouterLink
                    to='/forgot-password'
                    className='text-sm text-primary hover:text-primary/80 transition-colors'
                  >
                    Forgot password?
                  </RouterLink>
                </div>
                <PasswordInput
                  id='password'
                  name='password'
                  autoComplete='current-password'
                  required
                  placeholder='Enter your password'
                  value={formData.password}
                  onChange={handleChange}
                  className='form-input focus-ring transition-smooth'
                />
              </div>

              <Button
                type='submit'
                disabled={loading}
                className={`w-full btn-enhanced hover-lift focus-ring py-3 text-base font-medium ${loading ? 'btn-loading' : ''}`}
              >
                {loading ? (
                  <>
                    <Loader2 className='w-4 h-4 animate-spin' />
                    Signing In...
                  </>
                ) : (
                  <>
                    Sign In
                    <span className='ml-2'>→</span>
                  </>
                )}
              </Button>
            </form>

            {/* <div className='text-center text-sm pt-4 border-t border-gray-100'>
              <span className='text-gray-600'>
                Don't have an account?{' '}
                <RouterLink
                  to='/register'
                  className='font-medium text-primary hover:text-primary/80 transition-colors focus-ring rounded px-1 py-0.5'
                >
                  Sign up
                </RouterLink>
              </span>
            </div> */}
          </CardContent>
        </Card>

        {/* Trust indicators */}
        <div className='text-center space-y-4'>
          <div className='flex items-center justify-center space-x-6 text-xs text-gray-500'>
            <div className='flex items-center space-x-1'>
              <span className='w-2 h-2 bg-green-500 rounded-full'></span>
              <span>Secure Login</span>
            </div>
            <div className='flex items-center space-x-1'>
              <span className='w-2 h-2 bg-blue-500 rounded-full'></span>
              <span>Privacy Protected</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
